/**
 * Platform console shell — same responsive breakpoint convention as AppShell.
 * See AppShell.tsx for the canonical comment.
 */

import { useState, useEffect } from 'react';
import { Outlet, useNavigate, NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  BuildingOfficeIcon,
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  CpuChipIcon,
  UsersIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  InboxIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import {
  Dialog,
  DialogPanel,
  DialogBackdrop,
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { LocaleSwitcher } from '@/components/LocaleSwitcher';

/** Inner nav content — shared between the persistent sidebar and the drawer. */
function PlatformNav({ onNavClick }: { onNavClick?: () => void }) {
  const { t } = useTranslation(['admin', 'common']);

  const Item = ({
    to,
    icon: Icon,
    label,
    end,
  }: {
    to: string;
    icon: typeof UsersIcon;
    label: string;
    end?: boolean;
  }) => (
    <NavLink
      to={to}
      end={end}
      onClick={onNavClick}
      className={({ isActive }) =>
        clsx(
          'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-slate-800 text-white ring-1 ring-inset ring-white/10'
            : 'text-slate-400 hover:bg-slate-800/60 hover:text-white',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={clsx('h-5 w-5 shrink-0', isActive ? 'text-sky-400' : 'text-slate-500 group-hover:text-slate-300')}
            aria-hidden="true"
          />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );

  const SectionHeading = ({ label }: { label: string }) => (
    <p className="px-3 pb-1.5 pt-5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
      {label}
    </p>
  );

  return (
    <aside className="flex min-h-screen w-64 shrink-0 flex-col bg-slate-900 text-white">
      {/* Brand */}
      <div className="flex h-16 items-center gap-2.5 border-b border-slate-800 px-4">
        <img src="/favicon.svg" alt="" className="h-8 w-8 shrink-0 rounded-lg" />
        <div className="min-w-0">
          {/* The localized name is long (esp. Armenian) — small, may wrap. */}
          <span className="block text-xs font-semibold leading-snug tracking-tight text-white">
            {t('common:appName')}
          </span>
          <p className="mt-0.5 text-[11px] font-medium leading-none text-sky-400">
            {t('admin:platform.consoleSubtitle')}
          </p>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label={t('common:nav.platformNavigation')}>
        <div className="space-y-1">
          <Item to="/platform" end icon={Squares2X2Icon} label={t('admin:platform.overview.title')} />
        </div>

        <SectionHeading label={t('admin:platform.nav.clients')} />
        <div className="space-y-1">
          <Item to="/platform/companies" icon={BuildingOfficeIcon} label={t('admin:platform.companies.title')} />
          <Item to="/platform/users" icon={UsersIcon} label={t('admin:platform.users.title')} />
          <Item to="/platform/devices" icon={CpuChipIcon} label={t('admin:platform.devices.title')} />
        </div>

        <SectionHeading label={t('admin:platform.nav.finance')} />
        <div className="space-y-1">
          <Item to="/platform/billing" icon={BanknotesIcon} label={t('admin:platform.billing.title')} />
          <Item to="/platform/invoices" icon={DocumentTextIcon} label={t('admin:platform.invoices.title')} />
        </div>

        <SectionHeading label={t('admin:platform.nav.communication')} />
        <div className="space-y-1">
          <Item to="/platform/orders" icon={InboxIcon} label={t('admin:platform.orders.title')} />
          <Item to="/platform/telegram-chats" icon={ChatBubbleLeftRightIcon} label={t('admin:platform.telegramChats.title')} />
        </div>
      </nav>
    </aside>
  );
}

export function PlatformAppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { t } = useTranslation(['admin', 'common']);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change (covers back-button navigation too)
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* ── Desktop sidebar (lg+) ─────────────────────────────── */}
      <div className="hidden lg:flex">
        <PlatformNav />
      </div>

      {/* ── Mobile/tablet off-canvas drawer (< lg) ───────────── */}
      <Transition show={drawerOpen}>
        <Dialog onClose={() => setDrawerOpen(false)} className="relative z-50 lg:hidden">
          {/* Backdrop */}
          <TransitionChild
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop className="fixed inset-0 bg-black/50" />
          </TransitionChild>

          {/* Slide-in panel */}
          <div className="fixed inset-0 flex">
            <TransitionChild
              enter="transition ease-in-out duration-200 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-200 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <DialogPanel className="relative flex w-64 flex-col">
                {/* Close button */}
                <div className="absolute top-0 right-0 -mr-10 pt-3">
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(false)}
                    className="ml-1 flex h-8 w-8 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-white"
                    aria-label={t('common:actions.closeMenu')}
                  >
                    <XMarkIcon className="h-5 w-5 text-white" aria-hidden="true" />
                  </button>
                </div>
                <PlatformNav onNavClick={() => setDrawerOpen(false)} />
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      {/* ── Main content column ───────────────────────────────── */}
      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        {/* Topbar — dark navy */}
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-700 bg-slate-800 px-3 sm:px-4 lg:px-6">

          {/* Left: hamburger (< lg) + brand title (< lg) + user info (md+) */}
          <div className="flex items-center gap-3 min-w-0">
            {/* Hamburger — only on < lg */}
            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center rounded-md p-1.5 text-slate-300 hover:bg-slate-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500"
              onClick={() => setDrawerOpen(true)}
              aria-label={t('common:actions.openMenu')}
            >
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            {/* Brand title (< lg only) */}
            <div className="lg:hidden flex items-baseline min-w-0">
              <span className="text-sm font-semibold text-white truncate">
                {t('common:appName')}
              </span>
              <span className="ml-1.5 text-xs text-sky-400 shrink-0">Platform</span>
            </div>

            {/* User info — hidden on < md */}
            {user && (
              <div className="hidden md:flex items-center gap-3 min-w-0">
                <span className="inline-flex items-center rounded-full bg-sky-500/20 px-2.5 py-0.5 text-xs font-semibold text-sky-300 ring-1 ring-sky-500/40 shrink-0">
                  {t('admin:platform.roleBadge')}
                </span>
                <span className="truncate text-sm font-medium text-slate-100">{user.name}</span>
                <span className="hidden text-sm text-slate-400 lg:inline truncate">{user.email}</span>
              </div>
            )}
          </div>

          {/* Right: action buttons (md+) or avatar menu (< md) */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Full actions — md+ */}
            <div className="hidden md:flex items-center gap-2">
              <LocaleSwitcher variant="header" />
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-700"
                onClick={handleLogout}
              >
                {t('common:actions.signOut')}
              </Button>
            </div>

            {/* Avatar menu — < md only */}
            <div className="md:hidden">
              <Menu>
                <MenuButton
                  className="inline-flex items-center justify-center rounded-full p-1 text-slate-300 hover:bg-slate-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500"
                  aria-label={t('common:actions.openUserMenu')}
                >
                  <UserCircleIcon className="h-7 w-7" aria-hidden="true" />
                </MenuButton>

                <MenuItems
                  anchor="bottom end"
                  className="z-50 mt-1 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none divide-y divide-gray-100"
                >
                  {/* User info section */}
                  {user && (
                    <div className="px-4 py-3">
                      <p className="text-xs font-semibold text-sky-600 mb-1">{t('admin:platform.roleBadge')}</p>
                      <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                  )}

                  {/* Locale switcher row */}
                  <div className="px-4 py-2 flex items-center gap-2">
                    <span className="text-xs text-gray-500">{t('common:actions.language')}</span>
                    <LocaleSwitcher variant="header-light" />
                  </div>

                  {/* Action items */}
                  <div className="py-1">
                    <MenuItem>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                      >
                        {t('common:actions.signOut')}
                      </button>
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-slate-50 p-3 sm:p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
